import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {colors, fontSize, fontWeight, lineHeightRatio} from '@/styles/tokens';
import {cdnUrl} from '@/util/cdnImage';
import type {projectLaunchContent} from '@/util/contents/drawpost-data';

interface ProjectLaunchProps {
    heading: string;
    content: typeof projectLaunchContent;
}

const headingSx = {
    color: colors.text.primary,
    fontSize: {xs: fontSize[32], sm: fontSize[48]},
    lineHeight: lineHeightRatio.tight,
    mb: 3
} as const;

const bodyTextSx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeightRatio.looser,
    mb: 3
} as const;

const screenshotSx = {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    aspectRatio: '972/762'
} as const;

const rightColSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mt: {xs: 5, md: 0}
} as const;

const ProjectLaunch = memo(function ProjectLaunch({heading, content}: ProjectLaunchProps) {
    const screenshotImage = cdnUrl(content.screenshotPath);

    return (
        <Box className="top-space bottom-spacing mobile-top-padding mobile-padding-bottom">
            <MUIContainer>
                <Grid container spacing={{xs: 6, md: 8}} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography component="h2" sx={headingSx} className="font-headers">
                            {heading}
                        </Typography>

                        {content.paragraphs.map((text, index) => (
                            <Typography key={index} sx={bodyTextSx} className="font-regular-text remove-underline">
                                {text}
                            </Typography>
                        ))}
                    </Grid>

                    <Grid item xs={12} md={6} sx={rightColSx}>
                        <Box sx={screenshotSx}>
                            <Image
                                src={screenshotImage}
                                alt={content.screenshotAlt}
                                fill
                                loading="lazy"
                                style={{objectFit: 'cover'}}
                                sizes="(max-width: 900px) 100vw, 50vw"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
});

export default ProjectLaunch;
